import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import logo from "../assets/img/logo.png";
import loginCover from "../assets/img/login/cover.png";
import PasswordField from "../components/common/PasswordField";
import {
  clearAuthError,
  login,
  selectAuthError,
  selectAuthStatus,
  selectIsAuthenticated,
} from "../store/slices/authSlice";
import { validateLoginForm } from "../utils/validation";

const colorFlow = keyframes`
  0% { background-position: 0% 50%; opacity: 0; transform: translateY(12px); }
  40% { opacity: 1; transform: translateY(0); }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; opacity: 1; transform: translateY(0); }
`;

const AnimatedBrand = styled(Typography)(() => ({
  fontWeight: 700,
  letterSpacing: "-0.01em",
  backgroundImage:
    "linear-gradient(120deg, #10b981 0%, #0ea5e9 45%, #22d3ee 60%, #10b981 100%)",
  backgroundSize: "200% auto",
  color: "transparent",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  animation: `${colorFlow} 6s ease 10s infinite`,
  display: "inline-block",
}));

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const status = useSelector(selectAuthStatus);
  const authError = useSelector(selectAuthError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [formValues, setFormValues] = useState({ username: "", password: "" });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    if (authError) {
      dispatch(clearAuthError());
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const errors = validateLoginForm(formValues);
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      dispatch(
        login({
          username: formValues.username,
          password: formValues.password,
        })
      );
    }
  };

  const isLoading = status === "loading";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        backgroundColor: "#fffffa",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 3, sm: 6, md: 8 },
          py: { xs: 0, md: 0 },
          minHeight: { xs: "100vh", md: "auto" },
          flexGrow: 1,
        }}
      >
        <Stack
          spacing={2}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "100%",
            maxWidth: 420,
            backgroundColor: "rgba(255,255,255,0.86)",
            boxShadow: "0px 24px 64px rgba(15,23,42,0.08)",
            borderRadius: 1,
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 6 },
            alignItems: "center",
          }}
        >
          <AnimatedBrand variant="h5">Special Lane CMS</AnimatedBrand>
          <Box
            component="img"
            src={logo}
            alt="Special Lane CMS logo"
            sx={{ width: 88, height: 88, objectFit: "contain" }}
          />
          <Box textAlign="center" sx={{ width: "100%" }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Sign In
            </Typography>
            <Typography color="text.secondary">
              Enter your credentials to access the admin dashboard.
            </Typography>
          </Box>
          {authError && <Alert severity="error" sx={{ width: "100%" }}>{authError}</Alert>}
          <TextField
            label="Username"
            placeholder="Enter username"
            value={formValues.username}
            onChange={handleInputChange("username")}
            error={Boolean(formErrors.username)}
            helperText={formErrors.username}
            fullWidth
            required
            autoFocus
          />
          <PasswordField
            label="Password"
            placeholder="Enter password"
            value={formValues.password}
            onChange={handleInputChange("password")}
            error={Boolean(formErrors.password)}
            helperText={formErrors.password}
            fullWidth
            required
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isLoading}
            sx={{ width: "100%", mt: 1 }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "primary.contrastText" }} />
            ) : (
              "Sign In"
            )}
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: { xs: "none", md: "flex" },
          position: "relative",
          overflow: "hidden",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={loginCover}
          alt="Special Lane CMS cover"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 1,
          }}
        />
      </Box>
    </Box>
  );
};

export default LoginPage;
